import React, { useState, useEffect } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch } from 'src/hooks';
import { mfaSetup, mfaVerifySetup } from 'src/reducers/user';
import { Button, Input, Text } from 'src/shared';
import { Size } from 'src/shared/text';
import { Colors } from 'src/utils/color';
import { verboseLog } from 'src/utils/config';

interface MFASetupModalProps {
    show: boolean;
    onHide: () => void;
    onComplete: () => void;
}

const MFASetupModal: React.FC<MFASetupModalProps> = ({ show, onHide, onComplete }) => {
    const dispatch = useAppDispatch();
    const [step, setStep] = useState<'setup' | 'verify'>('setup');
    const [qrCode, setQrCode] = useState<string>('');
    const [secret, setSecret] = useState<string>('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const validationSchema = yup.object().shape({
        code: yup
            .string()
            .required('Verification code is required')
            .length(6, 'Code must be 6 digits')
            .matches(/^\d+$/, 'Code must contain only numbers')
    });

    const handleGenerateQR = async () => {
        setLoading(true);
        setErrorMessage('');
        
        try {
            const response = await dispatch(mfaSetup({})).unwrap();
            if (response?.success) {
                setQrCode(response.data.qr_code);
                setSecret(response.data.secret);
                verboseLog('✅ MFA QR code generated successfully');
            } else {
                setErrorMessage(response?.message || 'Failed to generate QR code');
            }
        } catch (err: any) {
            verboseLog('❌ MFA setup error:', err);
            setErrorMessage(err?.data?.message || 'Failed to generate QR code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && step === 'setup') {
            // Generate MFA secret and QR code
            handleGenerateQR();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, step]);

    const handleVerifyCode = async (code: string) => {
        console.log('🔐 MFA Verify Code - Starting verification with code:', code ? `${code.substring(0, 2)}****` : 'empty');
        verboseLog('🔐 MFA Verify Code - Starting verification with code:', code ? `${code.substring(0, 2)}****` : 'empty');
        setLoading(true);
        setErrorMessage('');
        
        try {
            console.log('🔐 MFA Verify Code - Dispatching mfaVerifySetup API call...');
            verboseLog('🔐 MFA Verify Code - Dispatching mfaVerifySetup API call...');
            const response = await dispatch(mfaVerifySetup({ code })).unwrap();
            console.log('🔐 MFA Verify Code - API response received:', response);
            verboseLog('🔐 MFA Verify Code - API response received:', response);
            
            if (response?.success) {
                console.log('✅ MFA setup verified successfully, backup codes received:', response.data?.backup_codes?.length || 0);
                verboseLog('✅ MFA setup verified successfully, backup codes received:', response.data?.backup_codes?.length || 0);
                setBackupCodes(response.data.backup_codes || []);
                setStep('verify');
                console.log('✅ MFA setup verified successfully - step changed to verify');
                verboseLog('✅ MFA setup verified successfully - step changed to verify');
            } else {
                console.log('❌ MFA verification failed:', response?.message);
                verboseLog('❌ MFA verification failed:', response?.message);
                setErrorMessage(response?.message || 'Invalid verification code');
            }
        } catch (err: any) {
            console.error('❌ MFA verification error:', err);
            verboseLog('❌ MFA verification error:', err);
            const errorMsg = err?.data?.message || err?.message || 'Invalid verification code. Please try again.';
            console.error('❌ MFA verification error message:', errorMsg);
            verboseLog('❌ MFA verification error message:', errorMsg);
            setErrorMessage(errorMsg);
        } finally {
            setLoading(false);
            console.log('🔐 MFA Verify Code - Finished (loading set to false)');
            verboseLog('🔐 MFA Verify Code - Finished (loading set to false)');
        }
    };

    const handleClose = () => {
        setStep('setup');
        setQrCode('');
        setSecret('');
        setBackupCodes([]);
        setErrorMessage('');
        onHide();
    };

    const handleComplete = () => {
        handleClose();
        onComplete();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Set Up Multi-Factor Authentication (MFA)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {step === 'setup' ? (
                    <div>
                        <Alert variant="info">
                            <strong>MFA is required for all accounts.</strong> Please set up MFA using Google Authenticator or any compatible authenticator app.
                        </Alert>
                        
                        {loading && !qrCode && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <Text>Generating QR code...</Text>
                            </div>
                        )}
                        
                        {qrCode && (
                            <div>
                                <Text marginBottom={15}>
                                    <strong>Step 1:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                                </Text>
                                
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <img 
                                        src={qrCode} 
                                        alt="MFA QR Code" 
                                        style={{ 
                                            maxWidth: '300px', 
                                            border: '2px solid #ddd',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            backgroundColor: 'white'
                                        }} 
                                    />
                                </div>
                                
                                <Text marginBottom={15} fontSize={Size.Small} color={Colors.extra.dark_gray}>
                                    <strong>Can't scan?</strong> Enter this secret key manually: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>{secret}</code>
                                </Text>
                                
                                <Text marginBottom={15}>
                                    <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app to verify setup:
                                </Text>
                                
                                <Formik
                                    initialValues={{ code: '' }}
                                    validationSchema={validationSchema}
                                    onSubmit={(values) => {
                                        console.log('🔐 Formik onSubmit called with code:', values.code ? `${values.code.substring(0, 2)}****` : 'empty');
                                        verboseLog('🔐 Formik onSubmit called with code:', values.code ? `${values.code.substring(0, 2)}****` : 'empty');
                                        handleVerifyCode(values.code);
                                    }}
                                >
                                    {({
                                        values,
                                        handleChange,
                                        handleSubmit,
                                        errors,
                                        touched
                                    }) => (
                                        <Form onSubmit={handleSubmit}>
                                            {errorMessage && (
                                                <Alert variant="danger" style={{ marginBottom: '15px' }}>
                                                    {errorMessage}
                                                </Alert>
                                            )}
                                            
                                            <Input
                                                label="Verification Code"
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                    const syntheticEvent = {
                                                        ...e,
                                                        target: { ...e.target, value, name: 'code' }
                                                    };
                                                    handleChange('code')(syntheticEvent);
                                                }}
                                                type="text"
                                                name="code"
                                                value={values.code}
                                                placeholder="000000"
                                            />
                                            {touched.code && errors.code && (
                                                <Text
                                                    marginLeft={50}
                                                    marginBottom={0}
                                                    color={Colors.extra.red}
                                                >
                                                    {errors.code}
                                                </Text>
                                            )}
                                            
                                            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                                <Button 
                                                    type="button" 
                                                    backgroundColor="#6c757d"
                                                    borderColor="#6c757d"
                                                    onClick={handleClose}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button type="submit" disabled={loading}>
                                                    {loading ? 'Verifying...' : 'Verify & Complete Setup'}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <Alert variant="success">
                            <strong>MFA Setup Complete!</strong>
                        </Alert>
                        
                        <Alert variant="warning" style={{ marginTop: '15px' }}>
                            <strong>⚠️ IMPORTANT: Save Your Backup Codes</strong>
                            <br />
                            These backup codes can be used to access your account if you lose access to your authenticator app.
                            <br />
                            <strong>Save them in a safe place - they will not be shown again!</strong>
                        </Alert>
                        
                        <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '15px', 
                            borderRadius: '6px',
                            marginTop: '15px',
                            marginBottom: '15px'
                        }}>
                            <Text fontWeight="bold" marginBottom={10}>
                                Your Backup Codes:
                            </Text>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)', 
                                gap: '10px',
                                fontFamily: 'monospace',
                                fontSize: '14px'
                            }}>
                                {backupCodes.map((code, index) => (
                                    <div key={index} style={{ 
                                        padding: '8px',
                                        backgroundColor: 'white',
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                        border: '1px solid #ddd'
                                    }}>
                                        {code}
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={handleComplete}>
                                I've Saved My Backup Codes
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default MFASetupModal;

